import React from 'react';
import { CustomField, DocTypeInfo } from '../types';

export interface SelectorState {
  mode: string; // a docType id (incl. 'generic') or 'custom'
  customFields: CustomField[];
  prompt: string;
}

interface DocTypeSelectorProps {
  docTypes: DocTypeInfo[];
  value: SelectorState;
  onChange: (next: SelectorState) => void;
  disabled?: boolean;
}

const FIELD_TYPES: CustomField['type'][] = ['string', 'number', 'integer', 'boolean'];

const emptyField = (): CustomField => ({ name: '', type: 'string', description: '' });

const DocTypeSelector: React.FC<DocTypeSelectorProps> = ({
  docTypes,
  value,
  onChange,
  disabled,
}) => {
  const patch = (partial: Partial<SelectorState>) => onChange({ ...value, ...partial });

  const updateField = (index: number, partial: Partial<CustomField>) => {
    const customFields = value.customFields.map((f, i) =>
      i === index ? { ...f, ...partial } : f
    );
    patch({ customFields });
  };

  const selected = docTypes.find((d) => d.id === value.mode);

  return (
    <div className="selector">
      <label className="label" htmlFor="docType">
        Document type
      </label>
      <select
        id="docType"
        className="input"
        value={value.mode}
        disabled={disabled}
        onChange={(e) => patch({ mode: e.target.value })}
      >
        {docTypes.map((d) => (
          <option key={d.id} value={d.id}>
            {d.label}
          </option>
        ))}
        <option value="custom">Custom fields…</option>
      </select>

      {value.mode !== 'custom' && selected && (
        <p className="muted selector__hint">
          {selected.description}
          {selected.fields.length > 0 && value.mode !== 'generic' && (
            <> Extracts: {selected.fields.join(', ')}.</>
          )}
        </p>
      )}

      {value.mode === 'custom' && (
        <div className="custom-fields">
          <p className="muted">Define the exact fields you want extracted.</p>
          {value.customFields.map((field, i) => (
            <div className="custom-fields__row" key={i}>
              <input
                className="input"
                placeholder="Field name (e.g. invoiceNumber)"
                value={field.name}
                disabled={disabled}
                onChange={(e) => updateField(i, { name: e.target.value })}
              />
              <select
                className="input input--type"
                value={field.type}
                disabled={disabled}
                onChange={(e) =>
                  updateField(i, { type: e.target.value as CustomField['type'] })
                }
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <input
                className="input"
                placeholder="Description (optional)"
                value={field.description ?? ''}
                disabled={disabled}
                onChange={(e) => updateField(i, { description: e.target.value })}
              />
              <button
                type="button"
                className="icon-btn"
                disabled={disabled || value.customFields.length === 1}
                title="Remove field"
                onClick={() =>
                  patch({ customFields: value.customFields.filter((_, j) => j !== i) })
                }
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="link-btn"
            disabled={disabled}
            onClick={() => patch({ customFields: [...value.customFields, emptyField()] })}
          >
            + Add field
          </button>
        </div>
      )}

      <label className="label" htmlFor="prompt">
        Extra instructions <span className="muted">(optional)</span>
      </label>
      <textarea
        id="prompt"
        className="input textarea"
        rows={2}
        placeholder="e.g. Dates are in DD/MM/YYYY; amounts are in EUR."
        value={value.prompt}
        disabled={disabled}
        onChange={(e) => patch({ prompt: e.target.value })}
      />
    </div>
  );
};

export { emptyField };
export default DocTypeSelector;
